import { IDialogProps } from 'Components/Core/Dialog/types';
import { ChangeEventWithData } from 'Components/Core/types';
import { RootState } from 'Data/Redux';
import { actions } from 'Data/ReduxViewModels/EquipmentInterfacing';
import { Severity } from 'Data/ViewModels/Core/types';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { combine } from 'Lib/Hooks/Selectors';
import { HiChevronDown, HiChevronUp } from 'Lib/Icons';
import deepEquals from 'Lib/Object/deepEquals';
import { replaceNonPrintableChars, resolveSpecialCharacterSequences } from 'Lib/String';
import classNames from 'classnames';
import { uniq } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, Field, Message, TextOutput } from '../../Core';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import './InterfaceDialog.scss';

interface ILogSection {
  requestId: string;
  message: string;
}

/**
 * Represents an event which triggered the closing of this dialog (to pass to the onClose handler)
 *
 * May be a keyboard or click event.
 */
type InterfaceDialogCloseEvent = React.SyntheticEvent & {
  data: {
    /**
     * Indicates whether this close event follows the acceptance of a new value.
     */
    valueAccepted: boolean;
  };
};

const LogSection: React.FC<ILogSection> = ({ requestId, message }) => {
  /**LogItems to store the read value and write value to com port */
  const logItems = useSelector(
    (state: RootState) => state.equipmentInterfacing.requests[requestId]?.logItems ?? [],
    deepEquals
  );
  const [displayLog, setDisplayLog] = useState(false);
  return (
    <div>
      <Button subtle onClick={() => setDisplayLog(!displayLog)} className="InterfaceDialog-LogDetailsButton">
        {message}
        {displayLog ? <HiChevronUp size="15px" /> : <HiChevronDown size="15px" />}
      </Button>
      {displayLog && (
        <div className="InterfaceDialog-LogItemContainer">
          {logItems.map((item, i) => (
            <p
              key={i}
              className={classNames('InterfaceDialog-LogItem', {
                'InterfaceDialog-LogItem-sent': item.type === 'Sent',
              })}
            >{`${item.type}: ${replaceNonPrintableChars(item.value)}`}</p>
          ))}
        </div>
      )}
    </div>
  );
};

interface IInterfaceDialog extends IDialogProps {
  portSettings?: any;
  equipment?: any;
  equipmentType: string;
  onChange?: (event?: ChangeEventWithData) => void;
  name?: string;
  label?: string;
  requestId: string;
  value?: any;
  onClose?: (event?: InterfaceDialogCloseEvent) => void;
}

const DialogComponent: React.FC<IInterfaceDialog> = ({
  equipment,
  equipmentType,
  label,
  onClose,
  onChange,
  requestId,
  visible,
  value: originalValue,
}) => {
  const dispatch = useDispatch();

  const regex: string = equipment?.Driver?.Data.Regex;
  const equipmentSerialOptions: SerialOptions = useMemo(
    () => ({
      baudRate: equipment?.PortSettings.BaudRate,
      dataBits: equipment?.PortSettings.DataBits,
      stopBits: equipment?.PortSettings.StopBits,
      parity: equipment?.PortSettings.Parity?.toLowerCase(),
    }),
    [
      equipment?.PortSettings.BaudRate,
      equipment?.PortSettings.DataBits,
      equipment?.PortSettings.StopBits,
      equipment?.PortSettings.Parity,
    ]
  );

  const isObtainingValue = useSelector(
    (state: RootState) => state.equipmentInterfacing.requests[requestId]?.obtainValue.isInProgress
  );
  const obtainedValue = useSelector(
    (state: RootState) => state.equipmentInterfacing.requests[requestId]?.obtainValue.obtainedValue
  );
  const hasObtainedValue = hasValue(obtainedValue) && !isObtainingValue;

  const hasObtainedValueFailed = useSelector(
    (state: RootState) => state.equipmentInterfacing.requests[requestId]?.obtainValue.failed
  );

  const resolvedRequest = resolveSpecialCharacterSequences(equipment?.Driver?.Data.Request);

  // Fire off an "automatic obtain" action once only.
  // We don't fire this off if we're already requesting a value (e.g. from focusing on an empty field)
  // or if we've already had a request fail
  const [autoObtainMade, setAutoObtainMade] = useState(isObtainingValue || hasObtainedValueFailed);
  useEffect(() => {
    if (!autoObtainMade) {
      dispatch(
        actions.obtainValue({
          requestId,
          equipmentId: equipment?.Id,
          options: equipmentSerialOptions,
          regexPattern: regex,
          send: resolvedRequest,
        })
      );
      setAutoObtainMade(true);
    }
  }, [dispatch, requestId, equipment?.Id, autoObtainMade, regex, equipmentSerialOptions, resolvedRequest]);

  // Cleanup request when dialog is closed
  useEffect(() => {
    return () => {
      dispatch(actions.cleanupRequest({ equipmentId: equipment?.Id, requestId: requestId }));
    };
  }, [dispatch, equipment?.Id, requestId]);

  const isSendInProgress = useSelector(
    (state: RootState) => state.equipmentInterfacing.requests[requestId]?.send.isInProgress
  );
  const statusMessage = isSendInProgress
    ? 'Sending request for value...'
    : isObtainingValue
    ? 'Awaiting expected value...'
    : hasObtainedValue
    ? 'Done'
    : 'Value was not received.';

  const messages = useSelector(
    combine(
      [
        (state: RootState) => state.equipmentInterfacing.requests[requestId]?.obtainValue.error,
        (state: RootState) => state.equipmentInterfacing.requests[requestId]?.send.error,
        (state: RootState) => state.equipmentInterfacing.requests[requestId]?.read.error,
      ],
      (...messages) =>
        uniq(messages.filter((m) => hasNonEmptyValue(m)).map((m) => m?.message)).map((message) => ({
          severity: 'error' as Severity,
          message: message,
        }))
    )
  );

  const acceptAction = (e: React.SyntheticEvent) => {
    if (typeof onChange === 'function') {
      (e as ChangeEventWithData).data = { value: obtainedValue, submitValues: true };
      onChange(e as ChangeEventWithData);
    }
    if (hasValue(onClose)) {
      (e as InterfaceDialogCloseEvent).data = { valueAccepted: true };
      onClose!(e as InterfaceDialogCloseEvent);
    }
  };

  const readAgainAction = () => {
    if (!isObtainingValue)
      dispatch(
        actions.obtainValue({
          requestId,
          equipmentId: equipment?.Id,
          options: equipmentSerialOptions,
          regexPattern: regex,
          send: resolvedRequest,
        })
      );
    else dispatch(actions.send({ requestId, equipmentId: equipment?.Id, value: resolvedRequest }));
  };

  const declineAction = (e: React.SyntheticEvent) => {
    dispatch(actions.readCancel({ requestId, equipmentId: equipment.Id }));

    if (hasValue(onClose)) {
      (e as InterfaceDialogCloseEvent).data = { valueAccepted: false };
      onClose!(e as InterfaceDialogCloseEvent);
    }
  };

  const DialogButtons = [
    <Button primary autoFocus={hasObtainedValue} disabled={!hasObtainedValue} onClick={acceptAction}>
      Accept
    </Button>,
    <Button light autoFocus={!hasObtainedValue} onClick={readAgainAction}>
      Read Again
    </Button>,
    <Button error onClick={declineAction}>
      Decline
    </Button>,
  ];

  return (
    <>
      <Dialog
        title={`From ${equipmentType} ${equipment.Code}`}
        width="450px"
        visible={visible}
        onClose={declineAction}
        buttons={DialogButtons}
      >
        <p className="InterfaceDialog-Header">
          Using {equipment?.Driver?.Data.Name} driver for {equipmentType}
        </p>
        <div className="InterfaceDialog-Content">
          <Field
            label={label ?? 'Received Value'}
            input={<TextOutput />}
            value={isObtainingValue ? 'Waiting...' : obtainedValue ?? originalValue}
            horizontal
          />
          <LogSection requestId={requestId} message={statusMessage} />
          <div className="InterfaceDialog-Messages">
            {messages.map((m) => (
              <Message severity={m.severity} message={m.message} border={false} key={m.message} />
            ))}
          </div>
        </div>
      </Dialog>
    </>
  );
};

const InterfaceDialog = withInjectedProps(DialogComponent);

export { InterfaceDialog };
export default InterfaceDialog;
